import { cast, Instance, SnapshotIn, types } from "mobx-state-tree";
import {
  createSurveyRuleQuestionKeyTypeModel,
  SurveyRuleQuestionKeyTypeModel,
} from "./SurveyRuleQuestionKeyTypeModel";
import {
  createSurveyRuleRespondentKeyTypeModel,
  SurveyRuleRespondentKeyTypeModel,
} from "./SurveyRuleRespondentKeyTypeModel";
import { SurveyRuleConditionEnums } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleCondition";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { SurveyRuleConditionValidationError } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleConditionValidationError";

export const SurveyRuleConditionModel = types
  .model("SurveyRuleConditionModel", {
    conditionId: types.identifier,
    keyType: types.union(
      SurveyRuleQuestionKeyTypeModel,
      SurveyRuleRespondentKeyTypeModel,
    ),
    connecter: types.enumeration(
      "connecter",
      Object.values(SurveyRuleConditionEnums.Connector.Connector),
    ),
    connectorValidationError: types.maybeNull(
      types.literal(
        SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
          .EXACT_ONE_CONDITION_MUST_HAVE_NONE_CONNECTOR,
      ),
    ),
  })
  .actions((store) => ({
    setKeyType: (keyType: "QUESTION" | "RESPONDENT"): void => {
      store.keyType = cast(
        keyType === "QUESTION"
          ? createSurveyRuleQuestionKeyTypeModel()
          : createSurveyRuleRespondentKeyTypeModel(),
      );
      store.connectorValidationError = null;
    },
    setConnector: (
      connector: SurveyRuleConditionEnums.Connector.Connector,
    ): void => {
      store.connecter = connector;
    },
    clearValidationErrors: (): void => {
      store.connectorValidationError = null;
      store.keyType.clearValidationErrors();
    },
    setValidationErrors: (
      validationErrors: SurveyRuleConditionValidationError.SurveyRuleConditionValidationError[],
    ): void => {
      validationErrors.forEach((validationError) => {
        switch (validationError) {
          case SurveyRuleConditionValidationError
            .SurveyRuleConditionValidationError
            .EXACT_ONE_CONDITION_MUST_HAVE_NONE_CONNECTOR:
            store.connectorValidationError = validationError;
            break;
          case SurveyRuleConditionValidationError
            .SurveyRuleConditionValidationError.INVALID_QUESTION_KEY:
          case SurveyRuleConditionValidationError
            .SurveyRuleConditionValidationError.INVALID_QUESTION_TYPE:
          case SurveyRuleConditionValidationError
            .SurveyRuleConditionValidationError
            .INVALID_QUESTION_KEY_GROUP_CHILD:
            if (store.keyType.type !== "QUESTION") {
              throw new Error(
                `The survey rule key type should be 'QUESTION' for the validation error '${validationError}'.`,
              );
            }
            store.keyType.setValidationError(validationError);
            break;
          case SurveyRuleConditionValidationError
            .SurveyRuleConditionValidationError.INVALID_RESPONDENT_KEY:
          case SurveyRuleConditionValidationError
            .SurveyRuleConditionValidationError.INVALID_RESPONDENT_VALUE:
            if (store.keyType.type !== "RESPONDENT") {
              throw new Error(
                `The survey rule key type should be 'RESPONDENT' for the validation error '${validationError}'.`,
              );
            }
            store.keyType.setValidationError(validationError);
            break;
          case SurveyRuleConditionValidationError
            .SurveyRuleConditionValidationError.INVALID_OPTION_CODE_VALUE:
            store.keyType.setValidationError(validationError);
            break;
          default:
            validationError satisfies never;
        }
      });
    },
  }));

export const createSurveyRuleConditionModel = (
  surveyRuleCondition?: SnapshotIn<typeof SurveyRuleConditionModel>,
): Instance<typeof SurveyRuleConditionModel> =>
  SurveyRuleConditionModel.create(
    surveyRuleCondition ?? {
      conditionId: new LeoUUID().uuid,
      keyType: createSurveyRuleQuestionKeyTypeModel(),
      connecter: SurveyRuleConditionEnums.Connector.Connector.NONE,
    },
  );
